import { localizedPageTitle } from '../localization/page-titles';
import { requestLanguage } from '../localization/request-language';
export type AnalyticsParams = Record<string, string | number | boolean | undefined>;
const measurementId = 'G-DK8FR2RYZ8';
const allowed = new Set(['membership_level', 'access_mode', 'book_id', 'book_title', 'menu_id', 'menu_name', 'parent_menu_id', 'source', 'outcome', 'reason', 'position_seconds', 'duration_seconds', 'position_percent', 'listening_seconds', 'audio_seconds', 'playback_rate', 'milestone', 'elapsed_ms', 'enabled', 'timer', 'feature']);
const metaEvents = new Set(['selfie_requested', 'selfie_created', 'audiobook_play_click', 'audiobook_started', 'audiobook_upgrade_click']);
['character_id', 'character_name', 'chat_mode', 'image_request_type'].forEach(key => allowed.add(key));
const characters = new Map<string, string>();
let lastPage = '';

export function pageDetails(path = window.location.pathname, search = window.location.search) {
  const section = path.split('/')[1];
  const titles: Record<string, string> = {
    '': 'Home', chats: 'Chats', saved: 'Saved Characters', price: 'Membership Plans',
    audiobooks: 'Audiobooks', setting: 'Settings', login: 'Log In', signup: 'Sign Up',
    support: 'Support', terms: 'Terms of Service', privacy: 'Privacy Policy',
    'successful-payment': 'Payment Complete',
  };
  const id = section === 'chat' ? path.split('/')[2] : undefined;
  const name = id ? characters.get(id) : undefined;
  const bookTalk = new URLSearchParams(search).get('bookTalk') === 'true';
  const context: AnalyticsParams = id && name ? {
    character_id: id, character_name: name, chat_mode: bookTalk ? 'book_talk' : 'chat',
  } : {};
  const title = id ? `${bookTalk ? 'Book Talk' : 'Chat'}${name ? ` with ${name}` : ''}` : titles[section] || 'Bookish Boyfriend';
  return {
    title: title === 'Bookish Boyfriend' ? title : `${title} | Bookish Boyfriend`,
    path: id && name ? `/chat/${encodeURIComponent(id)}${bookTalk ? '/book-talk' : ''}` : (section in titles ? `/${section}` : '/'),
    pending: Boolean(id && !name), context,
    excluded: section === 'admin' || section === 'callback',
  };
}

// Resolve public character metadata, never a user's conversation title or messages.
export function registerAnalyticsCharacter(id: string, name: string) {
  if (!id || !name) return;
  characters.set(id, name.slice(0, 100));
  if (window.location.pathname.split('/')[2] === id) trackPageView();
}

export function trackPageView() {
  const page = pageDetails();
  if (page.excluded) { lastPage = ''; return; }
  const language = requestLanguage();
  document.title = localizedPageTitle(page.title, language);
  if (page.pending) { lastPage = ''; return; }
  // Conversation IDs and incidental query updates are not new content pages.
  const pageKey = `${page.path}:${language}`;
  if (lastPage === pageKey) return;
  lastPage = pageKey;
  track('page_view', { source: analyticsPage(window.location.pathname), ...page.context });
}

export function analyticsPage(path: string): string {
  const page = path.split('/')[1];
  return ['chat', 'chats', 'saved', 'price', 'audiobooks', 'setting', 'login', 'signup', 'support'].includes(page) ? page : 'home';
}

export function membershipLevel(plan?: string): string {
  switch (String(plan || '').toLowerCase()) {
    case 'premium': return 'premium';
    case 'plus': case 'patron': return 'patron';
    case 'basic': case 'free': case 'subscriber': return 'subscriber';
    case 'guest': return 'guest';
    default: return 'unknown';
  }
}

// Analytics must never block the feature or receive prompts, identities, signed URLs or raw errors.
export function track(name: string, params: AnalyticsParams = {}) {
  try {
    if (!['bookishboyfriend.com', 'www.bookishboyfriend.com'].includes(window.location.hostname)) return;
    if (navigator.doNotTrack === '1' || (navigator as any).globalPrivacyControl === true) return;
    const page = pageDetails();
    if (page.excluded) return;
    const language = requestLanguage();
    const clean: AnalyticsParams = {};
    for (const [key, value] of Object.entries({ ...page.context, ...params })) {
      if (!allowed.has(key) || value === undefined) continue;
      if (typeof value === 'number' && Number.isFinite(value)) clean[key] = Math.round(value * 100) / 100;
      else if (typeof value === 'boolean') clean[key] = value;
      else if (typeof value === 'string') clean[key] = value.slice(0, 100);
    }
    // Selected site language, not browser language or caller-supplied metadata.
    clean.site_language = language;
    const host = window as any;
    const event = `bb_${name}`;
    // Nested payload avoids stale book/menu parameters persisting into later GTM events.
    try { (host.dataLayer = host.dataLayer || []).push({ event, bb: clean, bb_delivery: 'direct' }); } catch {}
    try {
      host.gtag?.('event', name === 'page_view' ? 'page_view' : event, { ...clean, send_to: measurementId,
        page_location: `${window.location.origin}${page.path}${language === 'de' ? '?lang=de' : ''}`,
        page_referrer: '', page_title: localizedPageTitle(page.title, language), transport_type: 'beacon',
        allow_google_signals: false, allow_ad_personalization_signals: false });
    } catch {}
    // Reading choices can be sensitive: no book, genre, character, or listening details to advertising.
    if (metaEvents.has(name)) {
      try { host.fbq?.('trackCustom', event, { membership_level: clean.membership_level || 'unknown' }); } catch {}
    }
  } catch {}
}

export function imageFailure(error: any): string {
  if (error?.image_limit || error?.monthly_limit || error?.monthly_limit_with_bonus || error?.exhausted_current_subscription) return 'quota';
  if (error?.premium_only || error?.pg_only || error?.upgrade_needed || error?.plus_trail_ended) return 'membership';
  if (error?.image_moderated) return 'provider_rejected';
  return 'request_failed';
}
